import { ChangeEvent, useMemo, useState } from 'react';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/free-solid-svg-icons/faEyeSlash';
import { ADDRESS_ATTRIBUTE_TYPE_FIELD_IDs, ATTRIBUTE_TYPE_FIELD_VALIDATIONS, DOB_ATTRIBUTE_TYPE_ID, NATIONALITY_ATTRIBUTE_TYPE_FIELD_ID, SSN4_ATTRIBUTE_TYPE_FIELD_ID, SSN9_ATTRIBUTE_TYPE_FIELD_ID } from '@constants/self-service-constants';

import { SelectFormGroup } from '@containers/common/select-form-group/select-form-group';
import { InputFormGroup } from '@containers/common/input-form-group/input-form-group';
import { SelectOption } from '@idw-types/common/select-option';
import { addMaxLength } from '@utils/self-service/prefilled-attribute-validation-util';
import { PrefilledAttributeTypeFieldValidationErrors } from '@idw-types/common/prefilled-attribute-validation-errors';
import { AxnAttributeTypeField } from '@idw-types/data/reference-data/axn';
import { LabelWidthTypes } from '@enums/label-width-types';
import styles from "../self-service-prefilled-attributes.less";
import { AttributeTypes, CustomAttributeTypeFieldTemplateTypes } from '@iddataweb/idw-common-ui/enums';
import { RadioInputFormGroup } from '@containers/common/radio-input-form-group/radio-input-form-group';
import { AXNUIContentCustomAttributeField } from '@iddataweb/idw-common-ui/types';
import { LayoutTypes } from '@enums/layout-types';
import { InputTypes } from '@enums/input-types';
import { US_STATE_SELECT_OPTIONS } from '@iddataweb/idw-common-ui/constants';
import { getCountryOptions } from '@utils/attribute-input-util';

type SelfServicePrefilledAttributeTypeFieldProps = {
    attributeTemplateName: string;
    attributeRequired: boolean;
    attributeField: AxnAttributeTypeField & { customization: { options: Array<SelectOption>; content: AXNUIContentCustomAttributeField; } };
    attributeTypeFieldValueMap: { [key: string]: string };
    attributeFieldValue: string;
    validationErrors: PrefilledAttributeTypeFieldValidationErrors;
    requestInProgress: boolean;
    onAttributeFieldChange: (attributeField: AxnAttributeTypeField, newValue: string) => void;
};

export const SelfServicePrefilledAttributeTypeField = (props: SelfServicePrefilledAttributeTypeFieldProps) => {
    const {
        attributeTemplateName,
        attributeRequired,
        attributeField,
        attributeTypeFieldValueMap,
        attributeFieldValue,
        validationErrors,
        requestInProgress,
        onAttributeFieldChange
    } = props;

    const handleUpdateTextInput = (event: ChangeEvent<HTMLInputElement>) => {
        onAttributeFieldChange(attributeField, event.target.value);
    };

    const handleUpdateSelectInput = (option: SelectOption) => {
        onAttributeFieldChange(attributeField, option?.value || null);
    };

    const handleUpdateRadioInput = (newValue: string) => {
        onAttributeFieldChange(attributeField, newValue);
    };

    const [shouldShowSSN, setShouldShowSSN] = useState<{ [id: string]: boolean }>({
        [SSN9_ATTRIBUTE_TYPE_FIELD_ID]: false,
        [SSN4_ATTRIBUTE_TYPE_FIELD_ID]: false
    });

    const toggleSsn = (id: string) => {
        setShouldShowSSN({ [id]: !shouldShowSSN[id] });
    };

    const getOthersProps = (attributeFieldId: string, shouldShowSSN: boolean) => {
        const isSSN = [SSN9_ATTRIBUTE_TYPE_FIELD_ID, SSN4_ATTRIBUTE_TYPE_FIELD_ID].includes(attributeFieldId);
        const maxLengthObject = addMaxLength(attributeFieldId);
        if (isSSN) {
            return {
                ...maxLengthObject,
                type: shouldShowSSN ? 'text' : 'password',
                afterLabelIconDisabled: false,
                afterInputIconDisabled: false,
                afterInputIconOnClick: () => toggleSsn(attributeFieldId),
                afterInputIcon: shouldShowSSN ? faEyeSlash : faEye
            }
        }

        return { ...maxLengthObject };
    };

    const {
        inputType,
        attributeLabel,
        selectOptions
    } = useMemo(() => {
        let inputType = InputTypes.TEXT;
        let selectOptions: Array<SelectOption> = [];
        let attributeLabel: string = attributeField.label;

       if (attributeField.id === ADDRESS_ATTRIBUTE_TYPE_FIELD_IDs.ADMINISTRATIVE_AREA_LEVEL_1) {
            inputType = InputTypes.SELECT;
            attributeLabel = "State";
            selectOptions = US_STATE_SELECT_OPTIONS;
        } else if (attributeField.id === ADDRESS_ATTRIBUTE_TYPE_FIELD_IDs.LOCALITY) {
            attributeLabel = "City";
        } else if (attributeField.id === ADDRESS_ATTRIBUTE_TYPE_FIELD_IDs.SUB_PREMISE) {
            attributeLabel = "Apartment Number";
        } else if (attributeField.id === ADDRESS_ATTRIBUTE_TYPE_FIELD_IDs.COUNTRY || attributeField.id === NATIONALITY_ATTRIBUTE_TYPE_FIELD_ID) {
            inputType = InputTypes.SELECT;
            selectOptions = getCountryOptions( attributeField.id === NATIONALITY_ATTRIBUTE_TYPE_FIELD_ID);
        } else if (attributeTemplateName === AttributeTypes.CUSTOM) {
            if (attributeField.customization) {
                selectOptions = attributeField.customization.options;
            }

            switch (attributeField.templateName) {
                case CustomAttributeTypeFieldTemplateTypes.CUSTOM_DROPDOWN:
                    inputType = InputTypes.SELECT;
                    break;
                case CustomAttributeTypeFieldTemplateTypes.CUSTOM_RADIO_BUTTON:
                    inputType = InputTypes.RADIO;
                    break;
                case CustomAttributeTypeFieldTemplateTypes.CUSTOM_ALPHA_NUMERIC:
                case CustomAttributeTypeFieldTemplateTypes.CUSTOM_NUMERIC:
                    inputType = InputTypes.TEXT;
                    break;
            }
        } else if (attributeField.attributeTypeID === DOB_ATTRIBUTE_TYPE_ID) {
            attributeLabel = null;
        }

        return {
            inputType,
            attributeLabel,
            selectOptions
        };
    }, [attributeField.attributeTypeID, attributeField.id, attributeTemplateName, attributeField.customization]);

    const selectedOption = useMemo(() => {
        return selectOptions.find(so => so.value === attributeTypeFieldValueMap?.[attributeField.id]) || null;
    }, [attributeTypeFieldValueMap?.[attributeField.id]]);

    const isRequired = useMemo(() => {
        if (attributeField.customization !== null) {
            return attributeRequired;
        } else if (attributeField.customization === null && ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeField.id]) {
            return ATTRIBUTE_TYPE_FIELD_VALIDATIONS[attributeField.id].isRequired(attributeRequired);
        } else {
            return false;
        }
    }, [attributeField.id, attributeField.customization, attributeRequired]);

    const getFieldByInputType = (inputType: InputTypes): React.ReactNode => {
        switch (inputType) {
            case InputTypes.SELECT: {
                return <SelectFormGroup
                    controlId={attributeField.id}
                    labelWidth={LabelWidthTypes.MEDIUM}
                    label={attributeLabel}
                    ariaLabel={attributeLabel}
                    automationId={`attribute-${attributeField.id}`}
                    selectedOption={selectedOption}
                    onChange={handleUpdateSelectInput}
                    disabled={requestInProgress}
                    isRequired={isRequired}
                    options={selectOptions}
                    isClearable={!isRequired}
                    isSearchable={true}
                />;
            }

            case InputTypes.TEXT: {
                return <InputFormGroup
                    controlId={attributeField.id}
                    labelWidth={LabelWidthTypes.MEDIUM}
                    label={attributeLabel}
                    ariaLabel={attributeLabel}
                    automationId={`attribute-${attributeField.attributeTypeID}-field-${attributeField.id}`}
                    placeholder={attributeField.description}
                    value={attributeFieldValue || ''}
                    onChange={handleUpdateTextInput}
                    disabled={requestInProgress}
                    isRequired={isRequired}
                    {...getOthersProps(attributeField.id, shouldShowSSN[attributeField.id])}
                />;
            }

            case InputTypes.RADIO: {
                return <RadioInputFormGroup
                    controlId={attributeField.id}
                    radioButtonLayout={LayoutTypes.COLUMN}
                    labelWidth={LabelWidthTypes.MEDIUM}
                    label={attributeLabel}
                    automationId={`attribute-${attributeField.attributeTypeID}-field-${attributeField.id}`}
                    value={selectedOption?.value || ""}
                    onChange={handleUpdateRadioInput}
                    disabled={requestInProgress}
                    isRequired={isRequired}
                    options={selectOptions}
                />;
            }

            default: {
                return null;
            }
        }
    }

    return (
        <div className={styles.formControl}>
            {getFieldByInputType(inputType)}
            {validationErrors && (
                <>
                    {Object.keys(validationErrors).map((key, index) => (
                        <div key={index}>
                            {validationErrors[key as 'pattern' | 'required']}
                        </div>
                    ))}
                </>
            )}
        </div>
    )
};
